import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TabsProps } from 'antd';
import type {
  LocationCreateEditModel,
  LocationModel,
  ServiceLocationConfigurationModel,
} from 'pos-shared/models/locationSchema.ts';

import { LocationAddressTab } from './LocationTabs/LocationAddressTab.tsx';
import { LocationBasicDetailsTab } from './LocationTabs/LocationBasicDetailsTab.tsx';
import { LocationLoginTab } from './LocationTabs/LocationLoginTab.tsx';
import { LocationSettingsTab } from './LocationTabs/LocationSettingsTab.tsx';
import type { ModeType } from './useModalType.tsx';

interface UseTabItemsParams {
  locationForm: UseFormReturn<LocationCreateEditModel>;
  isFirstTabComplete: boolean;
  isSecondTabComplete: boolean;
  location?: LocationModel;
  selectedCountry?: string;
  locationConfigurations?: ServiceLocationConfigurationModel[];
  isLoadingLocationConfigurations?: boolean;
  mode: ModeType;
  isOpen: boolean;
}

export const useLocationTabItems = ({
  locationForm,
  isFirstTabComplete,
  isSecondTabComplete,
  location,
  selectedCountry,
  locationConfigurations,
  isLoadingLocationConfigurations,
  mode,
}: UseTabItemsParams) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('1');

  const { setValue, watch } = locationForm;
  const locationValues = watch();

  useEffect(() => {
    if (!locationValues.configurations) {
      setValue('configurations', locationConfigurations);
    }
  }, [activeTab, locationConfigurations, isLoadingLocationConfigurations]);

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: `1. ${t('common.contact.basicDetails')}`,
      disabled: !location && !(activeTab === '1') && !isFirstTabComplete,
      children: (
        <LocationBasicDetailsTab locationForm={locationForm} mode={mode} />
      ),
    },
    {
      key: '2',
      label: `2. ${t('common.address.address')}`,
      disabled: !location && !(activeTab === '2') && !isSecondTabComplete,
      children: (
        <LocationAddressTab
          locationForm={locationForm}
          selectedCountry={selectedCountry}
        />
      ),
    },
    {
      key: '3',
      label: `3. ${t('common.words.settings')}`,
      disabled: !location && !(activeTab === '3'),
      children: <LocationSettingsTab locationForm={locationForm} />,
    },
    ...(mode === 'edit'
      ? [
          {
            key: '4',
            label: `4. ${t('clients.epicor.epicorLogin')}`,
            disabled: !location && !(activeTab === '5'),
            children: (
              <LocationLoginTab
                location={location}
                locationForm={locationForm}
              />
            ),
          },
        ]
      : []),
  ];

  return { tabItems, activeTab, setActiveTab };
};
